import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRxDB } from '../core/useRxDB'
import { updateAuditFields } from '../core/utils'
import type { UserPreferencesDocument } from '../../schemas/userPreferences'
import type {NotificationPreferences} from '@/lib/types/preferences';
import useLoginStore from '@/store/LoginStore'
import { logger } from '@/lib/monitoring/logger'
import {
  
  parseNotificationPreferences
} from '@/lib/types/preferences'

/**
 * Subscribe to the current user's preferences row for the active workspace.
 *
 * Pull seeds the row server-side, so once initial replication completes the
 * doc is guaranteed to exist. While replication is still in flight, `data` is
 * `null` and `isLoading` is `true`.
 */
export const useUserPreferences = () => {
  const { db } = useRxDB()
  const envId = useLoginStore((s) => s.workspaceId)
  const userId = useLoginStore((s) => s.userId)
  const [data, setData] = useState<UserPreferencesDocument | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!db || !envId || !userId) {
      setData(null)
      setIsLoading(!!db && !!envId && !!userId ? true : false)
      return
    }

    setIsLoading(true)

    const subscription = db.userpreferences
      .findOne({ selector: { envId, userId, isDeleted: false } })
      .$.subscribe({
        next: (doc) => {
          setData(doc ? (doc.toJSON() as UserPreferencesDocument) : null)
          setIsLoading(false)
        },
        error: (err: Error) => {
          logger.logError(err, 'DATABASE', {
            collection: 'userpreferences',
            operation: 'subscribe',
          })
          setIsLoading(false)
        },
      })

    return () => subscription.unsubscribe()
  }, [db, envId, userId])

  return { data, isLoading }
}

/**
 * Subscribe to the current user's notification preferences in normalized form.
 *
 * Display-only: every render normalizes the parsed JSON so missing event keys
 * fall back to canonical defaults. The normalized shape is NEVER auto-written
 * back to RxDB — persistence happens exclusively through useUpdateUserPreferences()
 * when the user explicitly toggles a setting, and through sync pull responses
 * after the backend has normalized server-side.
 */
export const useNotificationPreferences = (): {
  prefs: NotificationPreferences
  isLoading: boolean
  rawDoc: UserPreferencesDocument | null
} => {
  const { data, isLoading } = useUserPreferences()
  const prefs = useMemo(
    () => parseNotificationPreferences(data?.notificationsJson),
    [data?.notificationsJson],
  )
  return { prefs, isLoading, rawDoc: data }
}

/**
 * Patch the current user's preferences row. Performs a stale-revision-safe
 * update with retry. Audit fields are bumped automatically.
 */
export const useUpdateUserPreferences = () => {
  const { db } = useRxDB()
  const envId = useLoginStore((s) => s.workspaceId)
  const userId = useLoginStore((s) => s.userId)

  return useCallback(
    async (patch: Partial<UserPreferencesDocument>) => {
      if (!db || !envId || !userId) {
        throw new Error('Database or session not ready')
      }

      const maxRetries = 3
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        const doc = await db.userpreferences
          .findOne({ selector: { envId, userId, isDeleted: false } })
          .exec()

        if (!doc) {
          throw new Error('User preferences row not yet replicated. Try again shortly.')
        }

        try {
          await doc.update({
            $set: {
              ...patch,
              ...updateAuditFields(userId),
            },
          })
          return
        } catch (err: any) {
          if (err?.code === 'CONFLICT' && attempt < maxRetries - 1) {
            continue
          }
          logger.logError(err as Error, 'DATABASE', {
            collection: 'userpreferences',
            operation: 'update',
          })
          throw err
        }
      }
    },
    [db, envId, userId],
  )
}
